import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import { useDebouncedCallback } from 'use-debounce'

import { Pagination } from '@loadsmart/loadsmart-ui'
import { Header, Layout, PageContent, SpinnerWheel, type WCStack } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { RequestStatus } from 'core/domain/Request'
import { mapStringToStop } from 'core/infra/StopMapper'
import { Config, useConfig } from 'core/remote-config'
import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { type SearchState, useSearchState } from 'core/ui/hooks/useSearchState'

import { analytics } from '../../core/utils/analytics'
import {
  type AgnosticShipmentFilter,
  type DerivedShipmentStatus,
} from '../domain/AgnosticShipmentFilter'
import { AgnosticShipmentSort } from '../domain/AgnosticShipmentSort'
import { SHIPMENTS_PAGE_SIZE } from '../infra/ShipmentRepository'
import { AgnosticShipperListSection } from './AgnosticShipperListSection'
import { EmptyShipmentsListState } from './EmptyShipmentsListState'
import { ErrorShipmentsListState } from './ErrorShipmentsListState'
import { useAgnosticShipmentsList } from './hooks/useAgnosticShipmentsList'
import { type useAgnosticShippers } from './hooks/useAgnosticShippers'
import { type useGetShipmentsMetadata } from './hooks/useGetShipmentsMetadata'
import { ShipmentsMetadata } from './ShipmentsMetadata/ShipmentsMetadata'
import { ShipperLoadsFilter } from './ShipperLoadsFilter'
import { ShipperLoadsSort } from './ShipperLoadsSort'
import { TrackingOnboardingProvider } from './tracking/onboarding/TrackingOnboardingContext'
import UserNotOnboardedBanner from './tracking/onboarding/UserNotOnboardedBanner'
import UserNotOnboardedDialog from './tracking/onboarding/UserNotOnboardedDialog'
import UserOnboardedBanner from './tracking/onboarding/UserOnboardedBanner'
import UserOnboardedDialog from './tracking/onboarding/UserOnboardedDialog'

const ContentWrapper = styled.div`
  background-color: rgba(${getToken('color-background-secondary')});
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  min-height: 90vh;
`

function LoadingSpinner() {
  return (
    <Layout.Stack
      justify="center"
      align="center"
      style={{
        height: '100%',
      }}
      paddingT="spacing-6"
      data-testid="global-spinner"
    >
      <SpinnerWheel size="48" title="Loading..." />
    </Layout.Stack>
  )
}

export type SearchFilters = Omit<AgnosticShipmentFilter, 'pickup' | 'delivery'> & {
  pickup?: string
  delivery?: string
  page?: number
  status?: DerivedShipmentStatus[]
}

function navigateToPortalShipment(shipmentURL: string) {
  const { hash } = new URL(shipmentURL)

  window.location.hash = hash.replace('#/shipment', '#/portal/shipment')
}

export type ShipperLoadsPageProps = {
  useShipmentsList?: typeof useAgnosticShipmentsList
  useShippers?: typeof useAgnosticShippers
  useShipmentsMetadata?: typeof useGetShipmentsMetadata
  onShipmentClick?: (shipmentURL: string) => void
}

export function ShipperLoadsPage({
  useShipmentsList = useAgnosticShipmentsList,
  useShippers,
  useShipmentsMetadata,
  onShipmentClick = navigateToPortalShipment,
}: ShipperLoadsPageProps = {}) {
  const contentRef = useRef<WCStack>(null)
  const isUnreadChatEnabled = useConfig(Config.enableCarrierShipmentListChat)

  const [searchParams, setSearchParams] = useSearchState<SearchFilters>(false)
  const [searchInputValue, setSearchInputValue] = useState(searchParams.search ?? '')
  const [sort, setSort] = useState(AgnosticShipmentSort.Status)
  const abortController = useAbortController([searchParams, sort])
  const { status: listStatus, shipments, fetchAllShipments } = useShipmentsList()

  const totalCount = shipments.count
  const currentPage = Number(searchParams.page || 1)
  const pageCount = Math.ceil(totalCount / SHIPMENTS_PAGE_SIZE)

  const pickupStop = useMemo(() => mapStringToStop(searchParams.pickup), [searchParams.pickup])
  const deliveryStop = useMemo(
    () => mapStringToStop(searchParams.delivery),
    [searchParams.delivery]
  )

  const filterValues = useMemo<AgnosticShipmentFilter>(
    () => ({
      pickup: pickupStop,
      delivery: deliveryStop,
      pickupDateAfter: searchParams.pickupDateAfter,
      pickupDateBefore: searchParams.pickupDateBefore,
      status: searchParams.status,
      shipper: searchParams.shipper,
      mode: searchParams.mode,
      equipment: searchParams.equipment,
      tags: searchParams.tags,
      bol: searchParams.bol,
      po: searchParams.po,
      reference: searchParams.reference,
      so: searchParams.so,
      search: searchParams.search,
      pendingAppointments: searchParams.pendingAppointments,
      missingPods: searchParams.missingPods,
      missingInvoices: searchParams.missingInvoices,
      unreadChat: isUnreadChatEnabled ? searchParams.unreadChat : undefined,
    }),
    [pickupStop, deliveryStop, searchParams, isUnreadChatEnabled]
  )

  function handleReloadShipments() {
    fetchAllShipments(filterValues, sort, searchParams.page, abortController)
  }

  function handleFilterChange(values: SearchState | undefined) {
    if (values === undefined) return setSearchParams({})

    setSearchParams(prevState => ({
      ...prevState,
      ...values,
      page: undefined,
    }))
  }

  const updateSearchParams = useDebouncedCallback((searchTerm: string) => {
    setSearchParams(prevState => ({ ...prevState, search: searchTerm, page: undefined }))
  }, 500)

  const handleSearchChange = useCallback(
    (searchTerm: string) => {
      setSearchInputValue(searchTerm)
      updateSearchParams(searchTerm)
    },
    [updateSearchParams]
  )

  useEffect(() => {
    fetchAllShipments(filterValues, sort, searchParams.page, abortController)
  }, [fetchAllShipments, abortController, sort, filterValues, searchParams.page])

  useEffect(() => {
    if (listStatus === RequestStatus.Done) {
      analytics.track('Loaded: Command Center', {
        number_of_entities: totalCount,
        page_name: 'Shipments',
      })
    }
  }, [listStatus, totalCount])

  return (
    <ContentWrapper>
      <PageContent>
        <Header>
          <Header.Content>
            <Header.Title>Shipments</Header.Title>
          </Header.Content>
        </Header>
        <PageContent.Body>
          <Layout.Stack gap="spacing-8" ref={contentRef}>
            <ShipmentsMetadata
              filters={filterValues}
              onClick={handleFilterChange}
              shouldRenderIfMobile={false}
              useGetShipmentsMetadata={useShipmentsMetadata}
            />
            <ShipperLoadsFilter
              filters={filterValues}
              onChange={handleFilterChange}
              onSearchChange={handleSearchChange}
              searchQuery={searchInputValue}
              useShippers={useShippers}
              useShipmentsMetadata={useShipmentsMetadata}
            />
            <AsyncContent
              emptyState={<EmptyShipmentsListState />}
              errorState={<ErrorShipmentsListState onReloadShipments={handleReloadShipments} />}
              loadingState={<LoadingSpinner />}
              status={listStatus}
              count={totalCount}
            >
              <TrackingOnboardingProvider>
                <UserNotOnboardedBanner />
                <UserOnboardedBanner />
                <Layout.Stack gap="spacing-8" align="center">
                  <AgnosticShipperListSection
                    title=""
                    count={shipments.count}
                    shipments={shipments.results}
                    onActionClick={onShipmentClick}
                    actions={<ShipperLoadsSort value={sort} onChange={setSort} />}
                  />
                  {pageCount > 1 && (
                    <Pagination
                      boundaryCount={1}
                      count={pageCount}
                      page={currentPage}
                      siblingCount={3}
                      onChange={({ page }) => {
                        setSearchParams(prev => ({ ...prev, page }))
                      }}
                    />
                  )}
                  <UserNotOnboardedDialog />
                  <UserOnboardedDialog />
                </Layout.Stack>
              </TrackingOnboardingProvider>
            </AsyncContent>
          </Layout.Stack>
        </PageContent.Body>
      </PageContent>
    </ContentWrapper>
  )
}
